import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtINR, fmtDate, API } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { DownloadSimple, WhatsappLogo, FileText } from "@phosphor-icons/react";

function firstDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function lastDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); }

export default function ReportsHubPage({ initialTab = "customers" }) {
  const [tab, setTab] = useState(initialTab);
  return (
    <div data-testid="reports-hub">
      <PageHeader
        title="Reports & Exports"
        subtitle="Customer list, Tally export, GSTR-1. Download everything you need for sharing, marketing or filing."
      />
      <div className="px-8 pt-4 flex items-center gap-2 border-b border-border bg-surface">
        <TabBtn active={tab==="customers"} onClick={()=>setTab("customers")} testId="tab-customers">Customers</TabBtn>
        <TabBtn active={tab==="tally"} onClick={()=>setTab("tally")} testId="tab-tally">Tally Export</TabBtn>
      </div>
      <div className="p-8 max-w-6xl">
        {tab === "customers" && <CustomersTab />}
        {tab === "tally" && <TallyTab />}
      </div>
      <FieldStyle />
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors ${active ? "border-brand text-brand" : "border-transparent text-ink-muted hover:text-ink"}`}>
      {children}
    </button>
  );
}

function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/customer-activity");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/reports/customers.csv`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `customers.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Customer list downloaded");
    } catch { toast.error("Download failed"); }
  };

  const sendWa = (row) => {
    if (!row.phone) return toast.error("No phone number on file for this customer");
    const digits = String(row.phone).replace(/[^0-9]/g, "");
    const phone = digits.length === 10 ? `91${digits}` : digits;
    const text = `Namaste ${row.name},\n\nThis is a friendly reminder from Ramkishan Agri Innovate.\n\nOutstanding: ₹${row.outstanding.toFixed(2)} across ${row.invoice_count} invoice(s).\n\nThank you for your business.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-semibold">Customers & Activity</div>
          <div className="text-xs text-ink-muted">{rows.length} customers · billed / paid / outstanding summary</div>
        </div>
        <button onClick={downloadCsv} className="btn-primary inline-flex items-center gap-1.5" data-testid="dl-customers-csv"><DownloadSimple size={14} weight="bold" /> Download CSV (Name+Phone)</button>
      </div>

      <div className="table-wrapper">
        <table className="table-base">
          <thead><tr>
            <th>Name</th><th>Phone</th><th>GSTIN</th><th>City/State</th>
            <th className="text-right">Invoices</th>
            <th className="text-right">Billed</th>
            <th className="text-right">Paid</th>
            <th className="text-right">Outstanding</th>
            <th>Last Doc</th>
            <th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-6 text-ink-muted">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-ink-muted">No customers yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} data-testid={`cust-row-${r.id}`}>
                <td className="font-semibold">{r.name}</td>
                <td>{r.phone || "—"}</td>
                <td className="font-mono text-xs">{r.gstin || "—"}</td>
                <td>{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="text-right tabular">{r.invoice_count}</td>
                <td className="text-right tabular">{fmtINR(r.billed)}</td>
                <td className="text-right tabular text-[#1C4F32]">{fmtINR(r.paid)}</td>
                <td className={`text-right tabular font-semibold ${r.outstanding > 0 ? "text-destructive" : "text-ink-muted"}`}>{fmtINR(r.outstanding)}</td>
                <td className="text-xs">{fmtDate(r.last_doc_date)}</td>
                <td><button disabled={!r.phone} onClick={() => sendWa(r)} className="btn-ghost text-[#25D366] disabled:opacity-30 inline-flex items-center gap-1" data-testid={`wa-cust-${r.id}`}><WhatsappLogo size={16} weight="fill" /> Remind</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TallyTab() {
  const [from, setFrom] = useState(firstDayOfMonth());
  const [to, setTo] = useState(lastDayOfMonth());

  const download = async () => {
    try {
      const res = await fetch(`${API}/reports/tally-xml?from=${from}&to=${to}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `tally-vouchers-${from}-to-${to}.xml`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Tally XML downloaded");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="font-display font-semibold flex items-center gap-2"><FileText size={18} weight="duotone" className="text-brand" /> Export to Tally Prime / ERP 9</div>
        <div className="text-xs text-ink-muted mt-1">Generates a Tally-compatible XML voucher file. In Tally, go to <span className="font-semibold">Gateway → Import Data → Vouchers</span> and select this file.</div>
      </div>
      <div className="card-base p-5 flex items-end gap-4 flex-wrap">
        <div><div className="label-overline block mb-1.5">From</div><input type="date" className="field" value={from} onChange={e => setFrom(e.target.value)} data-testid="tl-from" /></div>
        <div><div className="label-overline block mb-1.5">To</div><input type="date" className="field" value={to} onChange={e => setTo(e.target.value)} data-testid="tl-to" /></div>
        <button onClick={download} className="btn-primary inline-flex items-center gap-1.5" data-testid="tl-download"><DownloadSimple size={14} weight="bold" /> Download Tally XML</button>
      </div>
      <div className="card-base p-5 bg-surface-muted/50">
        <div className="font-display font-semibold text-sm mb-2">What's included</div>
        <ul className="text-xs text-ink-muted space-y-1 list-disc pl-5">
          <li>All Tax Invoices + Repair Invoices in the date range (excluding cancelled / rejected)</li>
          <li>Party ledger auto-created from customer name</li>
          <li>Stock items, qty, rate and amount per line</li>
          <li>CGST / SGST / IGST ledger entries</li>
          <li>Narration, reference number, place of supply</li>
        </ul>
        <div className="text-xs text-destructive mt-3">Note: You may need to create the ledgers (CGST / SGST / IGST and Party names) in Tally first, or use "Create Masters" on import.</div>
      </div>
    </div>
  );
}
